import { types, Instance, flow, applySnapshot } from 'mobx-state-tree';
import { parsePhoneNumber } from 'react-phone-number-input/mobile';

import {
  updateDiscoveryDone,
  canUserSignUp,
  signUpUser,
  getUser,
  assignToGroup,
  userReDiscovery,
  updateAccountProfile,
  checkIfEmailExists,
  updateAccountAfterQuiz,
  sendQuoraConversionEvent,
} from './apiWrappers/wrappers/useDiscoveryResponse';
import {
  DiscoveryObject,
  SignUpData,
  AssignToGroupData,
  UpdateAccountProfileParams,
  UpdateAccountAfterQuizParams,
} from './types/UserDiscoveryResponse.types';

import { api } from '../index';
import { CriteriaMeta } from './Criteria';
import { toJS } from 'mobx';
import { ErrorType, recordError } from './apiWrappers/api/exceptions';
import { QuoraEventName } from './types/useQuora.types';

const UserDiscoveryResponseModel = types.model('UserDiscoveryResponseModel', {
  type: types.identifier,
  name: types.string,
  meta: types.maybeNull(CriteriaMeta),
});

interface UserDiscoveryResponse extends Instance<typeof UserDiscoveryResponseModel> {}
const UserDiscoveryResponseStoreModel = types
  .model('UserDiscoveryResponseStoreModel', {
    segment: types.maybeNull(types.string),
    groupId: types.maybeNull(types.string),
    utms: types.maybeNull(types.frozen()),
    userAnswers: types.array(UserDiscoveryResponseModel),
    leadId: types.maybeNull(types.string),
    accountPicture: types.maybeNull(types.string),
    authToken: types.maybeNull(types.string),
    accountId: types.maybeNull(types.string),
    userName: types.string,
    userEmail: types.string,
    userPhoneNumber: types.string,
    dateOfBirth: types.maybeNull(types.Date),
    gender: types.maybeNull(types.string),
    quoraClickId: types.maybeNull(types.string),
    userIp: types.maybeNull(types.string),
    userCountry: types.maybeNull(types.string),
    userRegion: types.maybeNull(types.string),
    userCity: types.maybeNull(types.string),
    anonymousId: types.maybeNull(types.string),
  })
  .views((self) => ({
    get userAnswersObject() {
      const discoveryObject: DiscoveryObject = {};
      self.userAnswers.forEach((answer) => {
        discoveryObject[answer.type] = answer.name;
      });
      return discoveryObject;
    },
    get planLabelsIdsFromAnswers() {
      const planLabelsRaw = self.userAnswers.reduce((acc, current) => {
        if (current.meta?.relatedPlanLabels) {
          return [...acc, ...current.meta.relatedPlanLabels] as string[];
        }

        return acc;
      }, [] as string[]);

      return Array.from(new Set(planLabelsRaw));
    },
    get signUpDateToSend() {
      const parsedPhone = parsePhoneNumber(self.userPhoneNumber);
      const voicesSegmentsRaw = self.userAnswers.reduce((acc, current) => {
        if (current.meta?.relatedSegments) {
          return [...acc, ...current.meta.relatedSegments] as string[];
        }

        return acc;
      }, [] as string[]);

      return {
        groupId: self.groupId,
        name: self.userName,
        phone: parsedPhone?.nationalNumber,
        country_code: '+' + parsedPhone?.countryCallingCode,
        email: self.userEmail,
        discovery: {
          ...this.userAnswersObject,
          ...self.utms,
          segment: self.segment,
          voicesSegments: Array.from(new Set(voicesSegmentsRaw)),
          planLabelsIds: this.planLabelsIdsFromAnswers,
        },
        organization: undefined,
        origin: '',
        ...this.userBirthDayAndGender,
      };
    },
    get userBirthDayAndGender() {
      let date: { [value: string]: string | Date } = {};
      if (self.dateOfBirth) {
        date.dateOfBirth = self.dateOfBirth;
      }
      if (self.gender) {
        date.gender = self.gender;
      }

      return date;
    },
  }))
  .actions((self) => ({
    updateAccountProfileData: flow(function* postData(data: UpdateAccountProfileParams) {
      const { kind } = yield updateAccountProfile(data);

      if (kind !== 'ok') {
        recordError(ErrorType.HANDLED, 'Setting account plan labels failed!');
      }

      return { kind };
    }),
    updateAccountAfterQuiz: flow(function* postData(data: UpdateAccountAfterQuizParams) {
      const { kind } = yield updateAccountAfterQuiz(data);

      if (kind !== 'ok') {
        recordError(ErrorType.HANDLED, 'Updating account after quiz failed!');
      }

      return { kind };
    }),
    sendQuoraConversionEvent: flow(function* postData({
      eventName,
      value,
    }: {
      eventName: QuoraEventName;
      value: number;
    }) {
      if (self.quoraClickId) {
        const props = {
          eventName,
          clickId: self.quoraClickId,
          value,
          user: {
            ...(self.userEmail && { email: self.userEmail }),
            ...(self.userPhoneNumber && { phone_number: self.userPhoneNumber }),
            ...(self.userName && { name: self.userName }),
            ...(self.dateOfBirth && { date_of_birth: self.dateOfBirth.toISOString() }),
            ...(self.userIp && { ip: self.userIp }),
            ...(self.userCountry && { country: self.userCountry }),
            ...(self.userRegion && { region: self.userRegion }),
            ...(self.userCity && { city: self.userCity }),
          },
        };
        const { kind } = yield sendQuoraConversionEvent(props);

        if (kind !== 'ok') {
          recordError(ErrorType.API, 'Send Quora conversion event failed');
        }

        return { kind };
      }
    }),
  }))
  .actions((self) => ({
    setUserNameAndEmail({ name, email }: { name: string; email: string }) {
      applySnapshot(self, { ...self, userEmail: email, userName: name });
    },
    setUserEmail(email: string) {
      applySnapshot(self, { ...self, userEmail: email });
    },
    setAuthToken(token: string | undefined) {
      if (!!token) {
        applySnapshot(self, { ...self, authToken: token });
        api.setAuthorizationHeader({ token });
      }
    },
    setQuoraClickId(clickID: string | undefined) {
      if (!!clickID) {
        applySnapshot(self, { ...self, quoraClickId: clickID });
      }
    },
    setUserIp(ip: string) {
      applySnapshot(self, { ...self, userIp: ip });
    },
    setUserCountry(country: string) {
      applySnapshot(self, { ...self, userCountry: country });
    },
    setUserRegion(region: string) {
      applySnapshot(self, { ...self, userRegion: region });
    },
    setUserCity(city: string) {
      applySnapshot(self, { ...self, userCity: city });
    },
    setUserPhoneNumber(phoneNumber: string) {
      self.userPhoneNumber = phoneNumber;
      applySnapshot(self, { ...self, userPhoneNumber: phoneNumber });
    },
    setAnonymousId(id: string) {
      self.anonymousId = id;
    },
    addUserAnswers(userAnswer: UserDiscoveryResponse) {
      self.userAnswers.push({
        meta: toJS(userAnswer.meta),
        name: userAnswer.name,
        type: userAnswer.type,
      });

      if (userAnswer.type === 'segment') {
        self.segment = userAnswer.name;
      }

      if (userAnswer.type.includes('gender')) {
        self.gender = userAnswer.name;
      }
    },
    setDateOfBirth(date: Date) {
      applySnapshot(self, { ...self, dateOfBirth: date });
    },
    removeUserAnswers(userAnswerKey: string) {
      self.userAnswers.pop();
      if (userAnswerKey === 'segment') {
        self.segment = null;
      }
    },
    removeGroupId() {
      applySnapshot(self, { ...self, groupId: null });
    },
    setUtms(utmString: string) {
      if (utmString && utmString.length > 0) {
        const utmsArray = utmString.split('&');
        const utmObject = utmsArray.reduce((accumulator, currentValue) => {
          const utmArray = currentValue.split('=');
          if (utmArray[0] === 'accessToken' || utmArray[0] === 'backUrl') return { ...accumulator };
          return { ...accumulator, [utmArray[0]]: utmArray[1] };
        }, {});
        applySnapshot(self, { ...self, utms: utmObject });
      }
    },
    getUser: flow(function* postData() {
      const { data, kind } = yield getUser();
      if (kind === 'ok') {
        const { id, email, phoneNumber, fullName } = data;
        applySnapshot(self, {
          ...self,
          accountId: id,
          userEmail: email,
          userPhoneNumber: phoneNumber,
          userName: fullName,
        });
      }
    }),
    signUpUser: flow(function* postData(userInformation: SignUpData) {
      const { data, kind } = yield signUpUser(userInformation);
      if (kind === 'ok') {
        const { leadId, accountId, accountPicture, token } = data;
        api.setAuthorizationHeader({ token });
        applySnapshot(self, { ...self, leadId, accountId, accountPicture, authToken: token });
      }

      const { planLabelsIds, voicesSegments } = userInformation.discovery;

      const planLabelsIdsParams = planLabelsIds && planLabelsIds.length > 0 ? Array.from(new Set(planLabelsIds)) : [];
      const voicesSegmentsParams =
        voicesSegments && voicesSegments.length > 0 ? Array.from(new Set(voicesSegments)) : [];

      const isSignupAfterQuiz = planLabelsIdsParams.length > 0 || voicesSegmentsParams.length > 0;

      if (isSignupAfterQuiz) {
        yield self.updateAccountAfterQuiz({
          planLabelIds: planLabelsIdsParams,
          voicesSegments: voicesSegmentsParams,
          segment: self.segment ?? undefined,
          createProgressFocus: true,
        });
      }

      return { data, kind };
    }),
    updateDiscoveryDone: flow(function* postData(discovery: DiscoveryObject) {
      const { data } = yield updateDiscoveryDone(discovery);

      if (data?.registrantId) {
        api.setAuthorizationHeader({ registrantId: data.registrantId });
      }
    }),
    assignUserToGroup: flow(function* fetchData(groupInfo: AssignToGroupData) {
      const { data, kind } = yield assignToGroup(groupInfo);
      if (kind === 'ok') {
        applySnapshot(self, { ...self, groupId: data.groupInstanceId });
      }

      return { data, kind };
    }),
    canUserSignUp: flow(function* fetchData(url: string) {
      const { kind, data } = yield canUserSignUp(url);
      return { kind, data };
    }),
    checkIfEmailExists: flow(function* fetchData(url: string) {
      const { kind } = yield checkIfEmailExists(url);

      if (kind === 'ok') {
        return { ok: true };
      }

      if (kind === 'rejected') {
        return {
          ok: false,
          accountExistsError: true,
        };
      }

      return {
        ok: false,
        accountExistsError: false,
      };
    }),
    userReDiscovery: flow(function* postData(discovery: DiscoveryObject) {
      const { kind } = yield userReDiscovery(discovery);

      if (kind !== 'ok') return;
      window.location.href = `${process.env.REACT_APP_WEBSITE}/onboarding/document/pick-a-group/${window.location.search}`;
    }),
  }));
export { UserDiscoveryResponseStoreModel, UserDiscoveryResponseModel, UserDiscoveryResponse };
